import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { useDialog } from "../../lib/dialog";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Loader, Card } from "./Dashboard";
import { Plus, Trash2, Pencil, Users, ChevronRight, ClipboardCheck, ShieldCheck, Drama, Calendar, MapPin } from "lucide-react";

const TYPE_LABEL = { dress_rehearsal: "Dress Rehearsal", safety_briefing: "Safety Briefing", other: "Session" };
const TYPE_ICON = { dress_rehearsal: Drama, safety_briefing: ShieldCheck, other: ClipboardCheck };
const EMPTY = { name: "", type: "dress_rehearsal", date: "", location: "" };

const fmtDate = (d) => {
  if (!d) return "No date set";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
};

export default function CastSessions() {
  const dialog = useDialog();
  const { isArchived } = useEvents();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => adminApi.get("/admin/cast-sessions").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (s) => { setEditingId(s.id); setForm({ name: s.name, type: s.type || "dress_rehearsal", date: s.date || "", location: s.location || "" }); setShowForm(true); window.scrollTo(0, 0); };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) return;
    if (editingId) await adminApi.patch(`/admin/cast-sessions/${editingId}`, form);
    else await adminApi.post("/admin/cast-sessions", form);
    closeForm(); load();
  };

  const del = async (s) => {
    if (await dialog.confirm({ title: "Delete this session?", description: `Attendance records for "${s.name}" will be lost.`, confirmLabel: "Delete", destructive: true })) {
      await adminApi.delete(`/admin/cast-sessions/${s.id}`); load();
    }
  };

  return (
    <AdminLayout title="Rehearsals & Briefings" subtitle="Create dress-rehearsal and safety-briefing sessions and sign your cast in on the day"
      actions={!isArchived && (
        <button onClick={() => (showForm ? closeForm() : openCreate())} data-testid="new-session-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">
          <Plus size={14} /> New Session
        </button>
      )}>
      {showForm && (
        <Card title={editingId ? "Edit Session" : "Create Session"} className="mb-4">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="label-mini block mb-1.5">Name</label>
              <input data-testid="session-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Dress Rehearsal — Night 1" className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
            </div>
            <div>
              <label className="label-mini block mb-1.5">Type</label>
              <select data-testid="session-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood">
                <option value="dress_rehearsal">Dress Rehearsal</option>
                <option value="safety_briefing">Safety Briefing</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="label-mini block mb-1.5">Date</label>
              <input data-testid="session-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
            </div>
            <div>
              <label className="label-mini block mb-1.5">Location</label>
              <input data-testid="session-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Optional" className="w-full bg-[#050505] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={save} data-testid="session-save-btn" className="px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]">{editingId ? "Save Changes" : "Save"}</button>
            <button onClick={closeForm} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] text-[#A19D94] hover:text-[#F2EBE1]">Cancel</button>
          </div>
        </Card>
      )}

      {!items ? <Loader /> : (
        <div className="space-y-2">
          {items.length === 0 && <p className="text-sm text-[#6B6862]">No sessions yet. Create a dress rehearsal or safety briefing to start signing your cast in.</p>}
          {items.map((s) => {
            const Icon = TYPE_ICON[s.type] || ClipboardCheck;
            return (
              <div key={s.id} onClick={() => navigate(`/admin/rehearsals/${s.id}`)} data-testid={`session-card-${s.id}`}
                className="cursor-pointer p-4 border border-[#1a1a1a] bg-[#0A0A0A] hover:border-blood transition-colors flex items-center gap-4">
                <div className="w-11 h-11 shrink-0 border border-[#262626] bg-[#050505] flex items-center justify-center text-blood"><Icon size={20} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase tracking-tight flex items-center gap-2">
                    {s.name}
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#1a1a1a] text-[#A19D94]">{TYPE_LABEL[s.type] || "Session"}</span>
                    {s.closed && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 bg-[#262626] text-[#A19D94]">Closed</span>}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-1 text-xs text-[#6B6862]">
                    <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(s.date)}</span>
                    {s.location && <span className="flex items-center gap-1"><MapPin size={11} /> {s.location}</span>}
                    <span className="flex items-center gap-1"><Users size={11} /> {s.expected || 0} cast · {s.signed_in_count || 0} signed in</span>
                  </div>
                </div>
                {!isArchived && (
                  <div className="flex items-center gap-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEdit(s)} data-testid={`session-edit-${s.id}`} className="text-[#6B6862] hover:text-[#F2EBE1]" title="Edit"><Pencil size={15} /></button>
                    <button onClick={() => del(s)} data-testid={`session-delete-${s.id}`} className="text-[#6B6862] hover:text-blood" title="Delete"><Trash2 size={15} /></button>
                  </div>
                )}
                <ChevronRight size={18} className="text-[#6B6862] shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
